import React, { useEffect, useMemo, useState } from 'react';
import { buildKeywordTopics, mentionDate, topicDefsFromEntries, topicDefsFromMonitored } from './topicKeywords';
import {
    bp_topics, bp_mentions, bp_get_retention_settings, bp_list_watchlist_entries,
    bp_watchlists_get, bp_watchlists_put,
} from '../../../services';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonModal from '../../../Components/CommonModal';
import CommonPagination from '../../../Components/CommonPagination';
import CommonLoader from '../../../Components/CommonLoader';
import StatusBadge from '../../../Components/StatusBadge';
import TopicTrendModal from './TopicTrendModal';
import { TakedownModal } from './MentionsTab';
import './brand-mentions.scss';
import { formatNumber, timeAgo, unwrap, EmptyState, SectionHeader, PLATFORM_ICON } from './_shared';
import { profileUrlFor } from './_avatarKit';
import TooltipText from '../../../Components/TooltipText';
import { Body, SubHeading } from '../../../Constants/Fonts';

const DAYS_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 14, label: 'Last 14 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
];
const SORT_OPTIONS = [
    { value: 'mentions', label: 'Mentions: high to low' },
    { value: 'engagement', label: 'Engagement: high to low' },
    { value: 'sov', label: 'Share of Voice: high to low' },
];

const MODAL_PAGE_SIZE = 25;
const MENTION_PAGE_SIZE = 100;
const MAX_MENTION_PAGES = 20;

async function loadMentions(days) {
    const fetchPage = page => bp_mentions({ days, page, page_size: MENTION_PAGE_SIZE, sort: '-posted_at' })
        .then(r => unwrap(r) || {});
    const first = await fetchPage(1);
    const pages = Math.min(first.pages || 1, MAX_MENTION_PAGES);
    const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => fetchPage(i + 2).catch(() => ({}))));
    return {
        mentions: [first, ...rest].flatMap(d => d.results || []),
        truncated: (first.pages || 1) > MAX_MENTION_PAGES,
    };
}

function TopicDetailsModal({ topic, days, onClose }) {
    const [mentions, setMentions] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [takedownTarget, setTakedownTarget] = useState(null);

    useEffect(() => {
        if (topic.matches) {
            setMentions(topic.matches.slice((page - 1) * MODAL_PAGE_SIZE, page * MODAL_PAGE_SIZE));
            setPages(Math.max(1, Math.ceil(topic.matches.length / MODAL_PAGE_SIZE)));
            setLoading(false);
            return;
        }
        setLoading(true);
        bp_mentions({
            days,
            narrative: topic.narrative_tag,
            page,
            page_size: MODAL_PAGE_SIZE,
            sort: '-posted_at',
        }).then(r => {
            const d = unwrap(r) || {};
            setMentions(d.results || []);
            setPages(d.pages || 1);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [topic.matches, topic.narrative_tag, days, page]);

    const handleTakedownSubmitted = (mentionId, td) => {
        setMentions(prev => prev.map(m => m.id === mentionId ? { ...m, takedown: td } : m));
    };

    const subtitle = (
        <>
            {formatNumber(topic.mentions)} mentions ·
            {topic.engagement != null && <> engagement {formatNumber(topic.engagement)} ·</>}
            {' '}{topic.share_of_voice_pct}% share of voice ·
            last {days} days
        </>
    );

    return (
        <CommonModal
            show={true}
            onHide={onClose}
            size="xl"
            title={topic.title}
            subtitle={subtitle}
        >
            <div>
                {topic.description && (
                    <Body className="bm_topic_modal_desc">{topic.description}</Body>
                )}

                <div className="bm_topic_modal_list">
                    {loading && <CommonLoader label="Loading mentions…" />}
                    {!loading && mentions.length === 0 && (
                        <Body as="div" className="bm_empty">No mentions in this window for this topic.</Body>
                    )}
                    {!loading && mentions.map(m => {
                        const postUrl = profileUrlFor(m.handle, m.platform) || m.url;
                        const td = m.takedown;
                        const tdActive = td && td.status !== 'resolved' && td.status !== 'failed';
                        return (
                            <div key={m.id} className="bm_topic_post">
                                <div className="bm_topic_post_head">
                                    <Body as="span" variant={2} weight="semibold" className="bm_topic_post_handle">{m.handle || '(no handle)'}</Body>
                                    <Body as="span" variant={2} className="bm_topic_post_meta">· {platformLabel(String(m.platform || '').toLowerCase())}</Body>
                                    <StatusBadge
                                        variant={m.sentiment === 'positive' ? 'success'
                                            : m.sentiment === 'negative' ? 'danger' : 'neutral'}
                                        dot={false}
                                        transform="uppercase"
                                        className="bm_topic_post_sentiment"
                                    >{m.sentiment || 'neutral'}</StatusBadge>
                                    <Body as="span" variant={2} className="bm_topic_post_meta">{timeAgo(m.posted_at)}</Body>
                                </div>
                                <Body className="bm_topic_post_text">{m.text}</Body>
                                <div className="bm_topic_post_actions">
                                    {postUrl && (
                                        <CommonButton
                                            text="View post →"
                                            variant="primary"
                                            className="bm_scan_btn bm_topic_post_btn"
                                            title="Open this post on the source platform in a new tab"
                                            onClick={() => window.open(postUrl, '_blank', 'noopener,noreferrer')}
                                        />
                                    )}
                                    <CommonButton
                                        text={tdActive ? `Takedown · ${td.status}` : 'Request takedown'}
                                        variant="error"
                                        className="bm_scan_btn bm_topic_post_btn"
                                        disable={td?.status === 'requested'}
                                        title={tdActive
                                            ? `Takedown already in flight (status: ${td.status})`
                                            : 'Request a takedown of this post'}
                                        onClick={() => setTakedownTarget(m)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!loading && pages > 1 && (
                    <CommonPagination
                        pageCount={pages}
                        forcePage={page - 1}
                        handlePageClick={({ selected }) => setPage(selected + 1)}
                    />
                )}
            </div>
            {takedownTarget && (
                <TakedownModal
                    mention={takedownTarget}
                    onClose={() => setTakedownTarget(null)}
                    onSubmitted={handleTakedownSubmitted}
                />
            )}
        </CommonModal>
    );
}

function TopicSparkline({ series }) {
    if (!series || series.length === 0) return null;
    const w = 120, h = 28, pad = 1;
    const values = series.map(s => s.mentions || 0);
    const max = Math.max(1, ...values);
    const stepX = (w - 2 * pad) / Math.max(1, values.length - 1);
    const x = i => pad + i * stepX;
    const y = v => h - pad - (v / max) * (h - 2 * pad);
    const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
    const maxIdx = values.indexOf(max);
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
            <path className="bm_topic_spark_line" d={path} />
            {/* highlight the peak so the cadence is readable at a glance */}
            <circle className="bm_topic_spark_peak" cx={x(maxIdx)} cy={y(max)} r="2.2" />
        </svg>
    );
}

function DeltaChip({ pct, isNew }) {
    if (isNew) {
        return <StatusBadge variant="info" dot={false} title="No mentions in the previous period">New</StatusBadge>;
    }
    if (pct == null) return null;
    if (Math.abs(pct) < 0.5) {
        return <StatusBadge variant="neutral" dot={false} transform="none">no change</StatusBadge>;
    }
    const up = pct > 0;
    return (
        <StatusBadge variant={up ? 'success' : 'danger'} dot={false} transform="none"
            title="Change in mentions vs the previous period of the same length">
            {up ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}% vs prior
        </StatusBadge>
    );
}

function relativeAge(iso) {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return 'just now';
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days}d ago`;
    const mon = Math.floor(days / 30);
    if (mon < 12) return `${mon}mo ago`;
    return `${Math.floor(mon / 12)}y ago`;
}

const PLATFORM_LABEL = {
    x: 'X', twitter: 'X', linkedin: 'LinkedIn', youtube: 'YouTube', tiktok: 'TikTok',
    reddit: 'Reddit', bluesky: 'Bluesky', facebook: 'Facebook', instagram: 'Instagram',
};
const platformLabel = p => PLATFORM_LABEL[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Other');

const ATTENTION_NEG_RATIO = 0.3;
const ATTENTION_MIN_NEG = 2;

const INFO_ICON = 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/hover-text.svg';
const SOV_HELP = 'Share of all mentions in this period that contain this topic. '
    + 'One post can mention several topics, so these don\'t add up to 100%.';

const HANDLE_CHARS = 9;
const shortHandle = h => {
    const s = String(h || '').replace(/^@+/, '').trim();
    if (!s) return '-';
    const chars = Array.from(s);   // don't split an emoji / accented letter
    return chars.length > HANDLE_CHARS ? `${chars.slice(0, HANDLE_CHARS).join('')}…` : s;
};

function TopPosts({ posts }) {
    if (!posts || posts.length === 0) return null;
    return (
        <div className="bm_topic_top">
            <Body as="div" variant={2} weight="semibold" className="bm_topic_label">Top posts</Body>
            {posts.map(m => {
                const href = profileUrlFor(m.handle, m.platform) || m.url;
                const s = String(m.sentiment || 'neutral').toLowerCase();
                const body = (
                    <>
                        <span className={`bm_topic_top_dot is-${s}`} title={`Sentiment: ${s}`} />
                        <Body as="span" variant={2} className="bm_topic_top_meta"
                            title={`${platformLabel(String(m.platform || '').toLowerCase())} · ${m.handle || '(no handle)'}`}>
                            <span className="bm_topic_top_platform" aria-hidden="true">
                                {PLATFORM_ICON[String(m.platform || '').toLowerCase()] || platformLabel(String(m.platform || '').toLowerCase()).charAt(0)}
                            </span>
                            {shortHandle(m.handle)}
                        </Body>
                        <Body as="span" variant={2} className="bm_topic_top_text">{m.text}</Body>
                        <Body as="span" variant={2} className="bm_topic_top_eng" title="Engagement (likes + comments + shares)">
                            {formatNumber(Number(m.engagement) || 0)} engagement
                        </Body>
                    </>
                );
                return href ? (
                    <a key={m.id} className="bm_topic_top_row" href={href}
                        target="_blank" rel="noopener noreferrer" title="Open on the source platform">{body}</a>
                ) : (
                    <div key={m.id} className="bm_topic_top_row">{body}</div>
                );
            })}
        </div>
    );
}

function Metric({ label, value, help, title }) {
    return (
        <div className="bm_topic_metric">
            <Body as="span" variant={2} className="bm_topic_label">
                {label}{help && <TooltipText src={INFO_ICON} alt="info" tooltipText={help} className="bm_topic_info" />}:
            </Body>
            <Body as="span" variant={2} weight="semibold" className="bm_topic_metric_value" title={title}>{value}</Body>
        </div>
    );
}

function TopicCard({ topic, onDrillIn, onOpenTrend, onRemove, removing }) {

    const ss = topic.sentiment || {};
    const sum = (ss.positive || 0) + (ss.neutral || 0) + (ss.negative || 0);
    const pct = (n) => sum ? Math.round((n / sum) * 100) : 0;
    const sparkline = topic.sparkline || [];
    const lastMatch = relativeAge(topic.last_match_at);
    const isEmpty = (topic.mentions || 0) === 0;
    const isNew = !isEmpty && topic.prior_mentions === 0;
    const needsAttention = (ss.negative || 0) >= ATTENTION_MIN_NEG
        && sum > 0 && ss.negative / sum >= ATTENTION_NEG_RATIO;

    return (
        <div className={`bm_topic_card cmn_elevated_card ${isEmpty ? 'is-empty' : ''}`}>
            <div className="bm_topic_head">
                <div className="bm_topic_head_main">
                    <SubHeading as="span" variant={2} className="bm_topic_name">{topic.title}</SubHeading>
                    <DeltaChip pct={topic.delta_pct} isNew={isNew} />
                    {needsAttention && (
                        <StatusBadge variant="danger" dot={false}
                            title={`${pct(ss.negative)}% of these mentions are negative`}>
                            Needs attention
                        </StatusBadge>
                    )}
                    {sparkline.length > 0 && (
                        <button type="button" className="bm_topic_spark"
                            title="Daily mentions in this period. Click for the detailed trend"
                            aria-label={`Open the detailed trend for ${topic.title}`}
                            onClick={() => onOpenTrend(topic)}>
                            <TopicSparkline series={sparkline} />
                        </button>
                    )}
                </div>
                <div className="bm_topic_actions">
                    {onRemove && (
                        <CommonButton text={removing ? 'Removing…' : 'Remove'} variant="danger"
                            disable={removing}
                            title="Stop tracking this keyword"
                            onClick={() => onRemove(topic)} />
                    )}
                    <CommonButton text="View posts →" variant="primary" className="bm_scan_btn"
                        onClick={() => onDrillIn(topic)}
                    />
                </div>
            </div>
            <Body className="bm_topic_description">{topic.description}</Body>
            {isEmpty ? (
                <Body as="div" variant={2} className="bm_topic_empty_state">
                    {lastMatch
                        ? <>Watching. Last match <strong>{lastMatch}</strong>, nothing in this window. Try widening the date range.</>
                        : <>Watching. No posts in this period contain this keyword yet. Try widening the date range.</>}
                </Body>
            ) : (
                <>
                    <div className="bm_topic_metric_grid">
                        <Metric label="Mentions" value={formatNumber(topic.mentions)} />
                        {topic.engagement != null && (
                            <Metric label="Engagement" value={formatNumber(topic.engagement)}
                                title={`${formatNumber(topic.likes)} likes · ${formatNumber(topic.comments)} comments · ${formatNumber(topic.shares)} shares`} />
                        )}
                        <Metric label="Share of Voice" value={`${topic.share_of_voice_pct}%`} help={SOV_HELP} />
                        {lastMatch && <Metric label="Last match" value={lastMatch} />}
                    </div>
                    {sum > 0 && (
                        <div className="bm_topic_sentiment">
                            <div className="bm_topic_sent_bar">
                                <span className="seg pos" style={{ width: pct(ss.positive) + '%' }} />
                                <span className="seg neu" style={{ width: pct(ss.neutral) + '%' }} />
                                <span className="seg neg" style={{ width: pct(ss.negative) + '%' }} />
                            </div>
                            <div className="bm_topic_sent_legend">
                                <Body as="span" variant={2} className="bm_topic_legend_item is-positive">● {pct(ss.positive)}% positive</Body>
                                <Body as="span" variant={2} className="bm_topic_legend_item is-neutral">● {pct(ss.neutral)}% neutral</Body>
                                <Body as="span" variant={2} className="bm_topic_legend_item is-negative">● {pct(ss.negative)}% negative</Body>
                            </div>
                        </div>
                    )}
                    {topic.platforms?.length > 0 && (
                        <div className="bm_topic_platforms">
                            {topic.platforms.map(p => (
                                <StatusBadge key={p.platform} variant="neutral" dot={false} transform="none">
                                    {platformLabel(p.platform)} · {formatNumber(p.count)}
                                </StatusBadge>
                            ))}
                        </div>
                    )}
                    <TopPosts posts={topic.top_posts} />
                </>
            )}
        </div>
    );
}

export default function TopicsTab() {
    const [days, setDays] = useState(14);
    const [sortBy, setSortBy] = useState('mentions');
    const [topics, setTopics] = useState([]);
    const [userTopics, setUserTopics] = useState([]);
    const [total, setTotal] = useState(0);
    const [openTopic, setOpenTopic] = useState(null);
    const [trendTopic, setTrendTopic] = useState(null);

    const [totalAllTime, setTotalAllTime] = useState(null);
    const [retentionDays, setRetentionDays] = useState(null);

    useEffect(() => {
        let alive = true;
        bp_get_retention_settings()
            .then(res => { if (alive) setRetentionDays(unwrap(res)?.retention_days ?? null); })
            .catch(() => { /* retention unknown - leave all ranges available */ });
        return () => { alive = false; };
    }, []);

    // Date-range options can't exceed the org's data-retention window: topics
    // older than `retentionDays` are purged, so a longer range returns nothing.
    // Hide any option longer than retention (keep-forever = null/0 keeps every
    // range), and clamp the current selection if it's now too long.
    const dayOptions = useMemo(() => {
        if (!retentionDays) return DAYS_OPTIONS;
        const withinRetention = DAYS_OPTIONS.filter(o => o.value <= retentionDays);
        return withinRetention.length ? withinRetention : [DAYS_OPTIONS[0]];
    }, [retentionDays]);

    useEffect(() => {
        if (retentionDays && days > retentionDays) {
            const maxWithin = dayOptions[dayOptions.length - 1];
            if (maxWithin && maxWithin.value !== days) setDays(maxWithin.value);
        }
    }, [retentionDays, days, dayOptions]);

    const sorter = useMemo(() => (a, b) =>
        sortBy === 'mentions' ? b.mentions - a.mentions
            : sortBy === 'sov' ? b.share_of_voice_pct - a.share_of_voice_pct
                : (b.engagement || 0) - (a.engagement || 0), [sortBy]);

    useEffect(() => {
        bp_topics({ days }).then(r => {
            const d = unwrap(r) || {};
            setTotal(d.total_mentions || 0);
            setTotalAllTime(typeof d.total_all_time_mentions === 'number'
                ? d.total_all_time_mentions : null);
        }).catch(() => { });
    }, [days]);

    const [windowData, setWindowData] = useState({ current: [], prior: [] });
    const [scanDefs, setScanDefs] = useState([]);
    const [topicsLoading, setTopicsLoading] = useState(true);
    const [topicsTruncated, setTopicsTruncated] = useState(false);
    useEffect(() => {
        let alive = true;
        setTopicsLoading(true);
        Promise.all([
            loadMentions(days * 2),
            bp_list_watchlist_entries({}).then(r => (unwrap(r) || {}).entries || []),
        ]).then(([{ mentions, truncated }, entries]) => {
            if (!alive) return;
            const since = Date.now() - days * 864e5;
            const current = [], prior = [];
            mentions.forEach(m => ((mentionDate(m) || 0) >= since ? current : prior).push(m));
            setWindowData({ current, prior });
            setScanDefs(topicDefsFromEntries(entries));
            setTopicsTruncated(truncated);
            setTopicsLoading(false);
        }).catch(() => { if (alive) { setWindowData({ current: [], prior: [] }); setTopicsLoading(false); } });
        return () => { alive = false; };
    }, [days]);

    const [monitored, setMonitored] = useState([]);
    useEffect(() => {
        bp_watchlists_get()
            .then(r => setMonitored((unwrap(r) || {}).monitored_topics || []))
            .catch(() => { });
    }, []);

    useEffect(() => {
        const { current, prior } = windowData;
        setTopics(buildKeywordTopics(current, prior, days, scanDefs).sort(sorter));
        setUserTopics(buildKeywordTopics(current, prior, days, topicDefsFromMonitored(monitored)).sort(sorter));
    }, [windowData, scanDefs, monitored, days, sorter]);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const [removingName, setRemovingName] = useState(null);
    const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    const removeKeyword = async (topic) => {
        const ok = await confirm({
            title: 'Remove keyword',
            message: `Stop tracking "${topic.title}"? Its card disappears from Topic Analysis and it is removed from Settings → Monitored topics. Posts are not deleted, and you can add it again any time.`,
            confirmLabel: 'Remove',
            danger: true,
            themeColor: false,
        });
        if (!ok) return;
        const next = monitored.filter(t => !sameName(t?.name, topic.keyword));
        setRemovingName(topic.keyword);
        try {
            const res = await bp_watchlists_put({ monitored_topics: next });
            const body = res?.statusCode || {};
            if (body.status !== 1) {
                notify(body.detail || body.error || 'Could not remove the keyword.', 'error');
                return;
            }
            setMonitored(next);
            notify(`Stopped tracking "${topic.title}".`, 'success');
        } catch (_) {
            notify('Could not remove the keyword.', 'error');
        } finally {
            setRemovingName(null);
        }
    };
    const [addOpen, setAddOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [addError, setAddError] = useState(null);
    const toList = raw => String(raw || '').split(',').map(x => x.trim()).filter(Boolean);
    const takenNames = new Set([
        ...monitored.map(t => String(t?.name || '').trim().toLowerCase()),
        ...scanDefs.map(d => d.name.toLowerCase()),
    ]);
    const addKeyword = async (values) => {
        const keyword = values.keyword.trim();
        const next = [...monitored, { name: keyword, keywords: [keyword], exclude: toList(values.exclude) }];
        setSaving(true); setAddError(null);
        try {
            const res = await bp_watchlists_put({ monitored_topics: next });
            const body = res?.statusCode || {};
            if (body.status !== 1) {
                setAddError(body.detail || body.error || 'Could not save the keyword.');
                return;
            }
            setMonitored(next);
            setAddOpen(false);
            notify(`Now tracking "${keyword}".`, 'success');
        } catch (_) {
            setAddError('Could not save the keyword.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="bm_topics_summary">
                <Body as="span" variant={2} className="bm_topics_summary_text">
                    {formatNumber(total)} mentions across {topics.length + userTopics.length} topics (last {days} days)
                    {totalAllTime != null && totalAllTime !== total && <> · {formatNumber(totalAllTime)} captured all-time</>}
                </Body>
                {totalAllTime != null && totalAllTime === total && total > 0 && (
                    <StatusBadge
                        variant="neutral"
                        dot={false}
                        transform="uppercase"
                        title={
                            `All ${total} captured mentions land inside this window. ` +
                            `Widening the lookback won't surface more topics, as this org ` +
                            `has no older data than ${days} days.`
                        }
                    >
                        All captured
                    </StatusBadge>
                )}
            </div>

            <div className="bm_filters">
                <CommonDropdown
                    ariaLabel="Date range"
                    value={days}
                    onChange={setDays}
                    options={dayOptions}
                />
                <CommonDropdown
                    ariaLabel="Sort"
                    value={sortBy}
                    onChange={setSortBy}
                    options={SORT_OPTIONS}
                />
                <span className="bm_topics_add">
                    <CommonButton text="+ Add keyword" variant="primary"
                        title="Track a keyword: every post containing it gets its own topic card"
                        onClick={() => { setAddError(null); setAddOpen(true); }} />
                </span>
            </div>

            <CommonModal
                show={addOpen}
                onHide={() => setAddOpen(false)}
                title="Add keyword"
                subtitle="Every post containing this keyword is grouped into its own topic card, with the same metrics as the others. Saved to Settings → Monitored topics; it only affects Topic Analysis, not scanning."
                fields={[
                    {
                        name: 'keyword', label: 'Keyword', type: 'text', required: 'Enter a keyword',
                        maxLength: { value: 128, message: 'Keep it under 128 characters' },
                        validate: v => {
                            const k = String(v || '').trim().toLowerCase();
                            if (!k) return 'Enter a keyword';
                            return !takenNames.has(k) || 'This keyword already has a card';
                        },
                    },
                    { name: 'exclude', label: 'Exclude posts containing (optional, comma-separated)', type: 'text' },
                ]}
                defaultValues={{ keyword: '', exclude: '' }}
                onSubmit={addKeyword}
                error={addError}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setAddOpen(false), disabled: saving },
                    { text: 'Add keyword', variant: 'primary', type: 'submit', loading: saving, loadingText: 'Saving…' },
                ]}
            />
            {toastNode}
            {confirmModal}

            {!topicsLoading && userTopics.length > 0 && (
                <>
                    <SectionHeader title="Your monitored topics" count={userTopics.length} />
                    <div className="bm_topics_list">
                        {userTopics.map(t => (
                            <TopicCard key={t.keyword} topic={t}
                                onDrillIn={topic => setOpenTopic(topic)}
                                onOpenTrend={topic => setTrendTopic(topic)}
                                onRemove={removeKeyword}
                                removing={removingName === t.keyword} />
                        ))}
                    </div>
                </>
            )}

            <SectionHeader title="Keyword topics" count={topics.length} />
            {topicsTruncated && !topicsLoading && (
                <Body variant={2} className="bm_topics_note">
                    Built from the {formatNumber(MAX_MENTION_PAGES * MENTION_PAGE_SIZE)} most recent
                    mentions. Narrow the date range for a complete picture.
                </Body>
            )}
            <div className="bm_topics_list">
                {topicsLoading && <CommonLoader label="Finding topics…" />}
                {!topicsLoading && topics.length === 0 && userTopics.length === 0 && (
                    <EmptyState
                        mark="bars"
                        title="No topics in this window"
                        body="Topics are your scan keywords: the brand handles, programmes and executives in Settings → Mention anchors. Add those, or add custom topics in Settings → Monitored topics to track specific phrases (e.g. xyz brand, campaign name, misinformation)."
                    />
                )}
                {!topicsLoading && topics.map(t => (
                    <TopicCard key={t.keyword} topic={t}
                        onDrillIn={topic => setOpenTopic(topic)}
                                onOpenTrend={topic => setTrendTopic(topic)} />
                ))}
                {openTopic && (
                    <TopicDetailsModal topic={openTopic} days={days}
                        onClose={() => setOpenTopic(null)} />
                )}                {trendTopic && (
                    <TopicTrendModal topic={trendTopic} days={days}
                        onClose={() => setTrendTopic(null)} />
                )}
            </div>
        </>
    );
}
